<section>
  <app-admin-page-header>
    <ng-container actions>
      @if (showCreate()) {
        <app-admin-button variant="primary" type="button" (click)="openCreate()" [disabled]="!canCreate()">
          Create platform user
        </app-admin-button>
      }
    </ng-container>
  </app-admin-page-header>

  <app-admin-table>
    <thead>
      <tr>
        <th>Name</th>
        <th>Email</th>
        <th>Role</th>
        <th>Google</th>
        <th>Status</th>
        <th>Last login</th>
        @if (showActionsColumn()) {
          <th style="text-align: right">Actions</th>
        }
      </tr>
    </thead>
    <tbody>
      @if (users().length === 0) {
        <tr>
          <td [attr.colspan]="showActionsColumn() ? 7 : 6">
            <app-admin-empty-state
              icon="users"
              title="No users yet"
              description="Create a platform user so they can sign in with Google."
            />
          </td>
        </tr>
      }
      @for (user of users(); track user._id) {
        <tr>
          <td><strong>{{ user.name }}</strong></td>
          <td>{{ user.email }}</td>
          <td><code class="cg-input--mono">{{ user.roleKey }}</code></td>
          <td>
            @if (googleLinked(user)) {
              <app-status-badge variant="published">Linked</app-status-badge>
            } @else {
              <app-status-badge variant="draft">Pending first login</app-status-badge>
            }
          </td>
          <td>
            <app-status-badge [variant]="user.isActive ? 'published' : 'draft'">
              {{ user.isActive ? 'Active' : 'Inactive' }}
            </app-status-badge>
          </td>
          <td>{{ user.lastLoginAt ? (user.lastLoginAt | date: 'medium') : '—' }}</td>
          @if (showActionsColumn()) {
            <td>
              <div class="cg-admin-table-actions">
                @if (showEdit()) {
                  <app-admin-button variant="ghost" type="button" (click)="openEdit(user)" [disabled]="!canEdit()">
                    Edit
                  </app-admin-button>
                }
                @if (showDeleteUser(user)) {
                  <app-admin-button variant="danger" type="button" (click)="remove(user)" [disabled]="!canDeleteUser(user)">
                    Delete
                  </app-admin-button>
                } @else if (showDelete() && user.userId === currentUserId()) {
                  <span class="cg-form-help">You</span>
                }
              </div>
            </td>
          }
        </tr>
      }
    </tbody>
  </app-admin-table>
</section>

@if (createModalOpen()) {
  <app-admin-modal title="Create platform user" (closed)="closeCreate()">
    <p class="cg-form-help cg-admin-field--full">
      The user can sign in with Google using this email. Their Google account is linked automatically on first login.
    </p>
    <div class="cg-admin-form-grid">
      <label class="cg-admin-field cg-admin-field--full">
        <span class="cg-admin-field__label">Name</span>
        <input
          class="cg-input"
          [(ngModel)]="createName"
          name="createName"
          autocomplete="name"
          placeholder="Full name"
        />
      </label>
      <label class="cg-admin-field cg-admin-field--full">
        <span class="cg-admin-field__label">Email</span>
        <input
          class="cg-input"
          type="email"
          [(ngModel)]="createEmail"
          name="createEmail"
          autocomplete="email"
          placeholder="name@company.com"
        />
      </label>
      <label class="cg-admin-field cg-admin-field--full">
        <span class="cg-admin-field__label">Role</span>
        <select class="cg-select" [(ngModel)]="createRoleKey" name="createRoleKey">
          <option value="PLATFORM_STAFF">PLATFORM_STAFF</option>
          <option value="COUNSELLOR">COUNSELLOR</option>
          <option value="SUPER_ADMIN">SUPER_ADMIN</option>
        </select>
      </label>
    </div>
    @if (createError()) {
      <p class="cg-form-error" role="alert">{{ createError() }}</p>
    }
    <div class="mt-4 flex flex-wrap justify-end gap-2">
      <app-admin-button variant="cancel" type="button" (click)="closeCreate()" [disabled]="createSaving()">
        Cancel
      </app-admin-button>
      <app-admin-button variant="primary" type="button" (click)="confirmCreate()" [disabled]="createSaving()">
        {{ createSaving() ? 'Creating…' : 'Create user' }}
      </app-admin-button>
    </div>
  </app-admin-modal>
}

@if (editingUser(); as user) {
  <app-admin-modal title="Edit platform user" (closed)="closeEdit()">
    <p class="cg-form-help cg-admin-field--full">
      You can change name and role. Email stays the same. After a role change, the user must log in again to get that role’s permissions.
    </p>
    <div class="cg-admin-form-grid">
      <label class="cg-admin-field cg-admin-field--full">
        <span class="cg-admin-field__label">Name</span>
        <input
          class="cg-input"
          [(ngModel)]="editName"
          name="editName"
          autocomplete="name"
          placeholder="Full name"
        />
      </label>
      <div class="cg-admin-field cg-admin-field--full">
        <span class="cg-admin-field__label">Email</span>
        <p class="cg-form-help">{{ user.email }}</p>
      </div>
      <label class="cg-admin-field cg-admin-field--full">
        <span class="cg-admin-field__label">Role</span>
        <select class="cg-select" [(ngModel)]="editRoleKey" name="editRoleKey">
          @for (opt of roleOptions; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>
      </label>
    </div>
    @if (editError()) {
      <p class="cg-form-error" role="alert">{{ editError() }}</p>
    }
    <div class="mt-4 flex flex-wrap justify-end gap-2">
      <app-admin-button variant="cancel" type="button" (click)="closeEdit()" [disabled]="editSaving()">
        Cancel
      </app-admin-button>
      <app-admin-button variant="primary" type="button" (click)="confirmEdit()" [disabled]="editSaving()">
        {{ editSaving() ? 'Saving…' : 'Save' }}
      </app-admin-button>
    </div>
  </app-admin-modal>
}
